// components/portal/ChatContextDashboard.tsx
import React, { useCallback, useMemo } from 'react';
import { STUDIO_PROJECT_KINDS, canAccessStudioKind } from '#lib/studio-project-view';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '../entities/EntityView';
import { PortfoliosTable } from '../entities/tables/portfolios/PortfoliosTable';
import { UnitsView } from '../entities/tables/units/UnitsTable';
import { MarketSignalsTable } from '../entities/tables/marketSignals/MarketSignalsTable';
import { BrandGeoFolders } from './BrandGeoFolders';
import { PropertyRegionFolders } from './PropertyRegionFolders';
import { ContactOrgFolders } from './ContactOrgFolders';
import { DealsTable } from '../entities/tables/deals/DealsTable';
import { AuditsTable } from '../entities/tables/audits/AuditsTable';
import { LegalEntitiesTable } from '../entities/tables/legalEntities/LegalEntitiesTable';
import { OrganisationsTable } from '../entities/tables/organisations/OrganisationsTable';
import { ProjectsGrid } from '../entities/grids/ProjectsGrid';
import { PortfolioFitsTable } from '../entities/tables/portfolioFits/PortfolioFitsTable';
import { PortfolioFitsGrid } from '../entities/grids/PortfolioFitsGrid';
import { SourcingCenterView } from './SourcingCenterView';
import { SourceRentRoll } from './SourceRentRoll';
import type { InventoryVisualMode } from './ViewModeGraphicToggle';
import { normalizeUserActivities, LEGACY_COMPAT_USER_ACTIVITIES } from '#lib/user-activities';
import { PageEnter } from '@/components/ui/PageEnter';
import {
  isTableFocusSubject,
  resolvePortalViewFromTableFocus,
} from '#lib/leasing/session-focus';

export const ChatContextDashboard = () => {
  const { kernel } = useKernel();
  const activeSession = useKernelStore((state) => {
    const activeSessionId = state.activeChatSessionId;
    return activeSessionId ? state.inventory.ChatSession.registry[activeSessionId] ?? null : null;
  });
  const userContext = useKernelStore((state) => state.userContext);
  const studioSection = useKernelStore((state) => state.studioSection);
  const canAccessDesign = useKernelStore(state => normalizeUserActivities(state.userPreferences.activities, { fallback: LEGACY_COMPAT_USER_ACTIVITIES }).includes('DESIGN'));
  const projectKind = studioSection === 'factory' ? 'list' : studioSection;
  const projectKindFilter = useMemo(() => ({ projectKind } as const), [projectKind]);
  const activeAction = activeSession?.activeSubject ?? null;
  const projectsInitialFilters = useMemo(
    () => (userContext.dbUserId ? { userId: userContext.dbUserId } : undefined),
    [userContext.dbUserId],
  );
  const portfolioFitsFocusRuntimeMode = useMemo<InventoryVisualMode>(() => {
    const entry = activeSession?.focusRuntimeByKey?.['table:portfolioFits'] as
      | { presentationMode?: unknown }
      | undefined;
    return entry?.presentationMode === 'table' ? 'table' : 'grid';
  }, [activeSession?.focusRuntimeByKey]);
  const setPortfolioFitsFocusRuntimeMode = useCallback((mode: InventoryVisualMode) => {
    kernel.current?.setSessionTableFocusPresentationMode({
      sessionId: kernel.current?.getActiveChatSessionId() ?? null,
      tableView: 'portfolioFits',
      entityType: 'PortfolioFit',
      presentationMode: mode,
    });
  }, [kernel]);

  if (!activeSession) return null;

  const renderTableFocusInterface = () => {
    if (!activeAction || !isTableFocusSubject(activeAction)) return null;
    const tableView = resolvePortalViewFromTableFocus(activeAction);
    if (!tableView) return null;

    switch (tableView) {
      case 'properties':
        // Assets page = folder browser (Région → Type d'actif → scoped table). The raw
        // unscoped table is the admin table browser's business, and it renders it itself.
        return <PropertyRegionFolders />;
      case 'units':
        return <UnitsView />;
      case 'marketSignals':
        return <MarketSignalsTable />;
      case 'brands':
        // Tenants page = geographic folder browser (Region → Country → brands).
        return <BrandGeoFolders />;
      case 'contacts':
        // Contacts page = folder browser by organisation (tenant) → scoped table.
        return <ContactOrgFolders />;
      case 'portfolios':
        // Portfolios are locked to List (table) view, like Assets.
        return <PortfoliosTable />;
      case 'portfolioFits':
        return portfolioFitsFocusRuntimeMode === 'table' ? (
          <PortfolioFitsTable
            viewModeVisual={portfolioFitsFocusRuntimeMode}
            onViewModeVisualChange={setPortfolioFitsFocusRuntimeMode}
          />
        ) : (
          <PortfolioFitsGrid
            viewModeVisual={portfolioFitsFocusRuntimeMode}
            onViewModeVisualChange={setPortfolioFitsFocusRuntimeMode}
          />
        );
      case 'deals':
        return <DealsTable />;
      case 'audits':
        return <AuditsTable />;
      case 'legalEntities':
        return <LegalEntitiesTable />;
      case 'projects': {
        if (!canAccessDesign || !canAccessStudioKind(projectKind, userContext.permissions)) return null;
        return <ProjectsGrid key={projectKind} initialFilters={projectsInitialFilters} lockedFilters={projectKindFilter}
          assetLedgerTitle={STUDIO_PROJECT_KINDS[projectKind].label} assetLedgerBreadcrumb={`Studio / ${STUDIO_PROJECT_KINDS[projectKind].label}`} />;
      }
      case 'organisations':
        return <OrganisationsTable />;
      default: {
        const exhaustiveCheck: never = tableView;
        return exhaustiveCheck;
      }
    }
  };

  const renderActionInterface = () => {
    if (!activeAction) {
      return null;
    }

    const tableInterface = renderTableFocusInterface();
    if (isTableFocusSubject(activeAction)) {
      return (
        <div className="relative h-full w-full bg-transparent">
          {tableInterface}
        </div>
      );
    }

    // A PROPERTY GOES TO SOURCE WHATEVER BROUGHT IT HERE. Portfolio and Unit still have pages, so
    // they only divert here on an explicit sourcing focus. An asset does not: its page was
    // deleted, Source is the only surface it has, and without this clause every other way of
    // focusing one - a data-gap action from the Action Center, a session whose subject was stored
    // before the page was removed - landed on a single inventory row stranded on an empty screen.
    if (activeAction.entityType === 'Property' && activeAction.entityId && activeAction.focusActionType === 'RENT_ROLL') {
      return <SourceRentRoll key={activeAction.entityId} propertyId={activeAction.entityId} />;
    }
    const isUnifiedSourcingFocus =
      Boolean(activeAction.entityId) &&
      (activeAction.entityType === 'Property' ||
        (activeAction.focusActionType === 'ORCHESTRATE_SOURCING' &&
          (activeAction.entityType === 'Portfolio' || activeAction.entityType === 'Unit')));

    if (isUnifiedSourcingFocus) {
      return (
        <SourcingCenterView
          entityType={activeAction.entityType as 'Portfolio' | 'Property' | 'Unit'}
          entityId={activeAction.entityId!}
        />
      );
    }

    if (!activeAction.entityId) {
      return (
        <div className="p-8">
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
            The active task has no entity context. Select another action.
          </div>
        </div>
      );
    }

    return (
      <EntityView
        type={activeAction.entityType}
        entityId={activeAction.entityId}
        focusActionType={activeAction.focusActionType}
        variant="page"
      />
    );
  };

  return (
    <div className="flex h-full w-full flex-col bg-black">
      <div className="custom-scrollbar flex-1 overflow-y-auto bg-black">
        <PageEnter pageKey={activeAction ? `${activeAction.entityType}:${activeAction.entityId ?? ''}:${activeAction.focusActionType ?? ''}:${studioSection}` : ''} className="h-full w-full">
          {renderActionInterface()}
        </PageEnter>
      </div>
    </div>
  );
};
